import { Trial } from "@/types/trial";
import { timestampToMillis } from "@shared/deferredAnalysis";
import { clsx, type ClassValue } from "clsx";
import type { Timestamp } from "firebase/firestore";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function clampValue(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

/**
 * What a "Timestamp" field can actually hold once it comes back from Firestore:
 * a real `Timestamp`, or one of the plain maps produced by mobile writes and
 * admin-SDK exports. Declaring this lets callers pass what they really have
 * instead of casting past the compiler.
 */
export type TimestampLike =
  | Timestamp
  | { seconds?: number; nanoseconds?: number; _seconds?: number; _nanoseconds?: number }
  | null
  | undefined;

/**
 * A usable `Date`, or `undefined` — **never an Invalid Date**. The three helpers
 * below are the supported way to read a Firestore timestamp in any render or sort
 * path; do not call `.toDate()`/`.toMillis()` directly.
 *
 * `Timestamp` fields are declared non-optional, but documents come from an
 * unvalidated `{ id, ...data } as Trial` cast in `useFirestore`, so that guarantee
 * does not hold at runtime — and with no error boundary in the app, one bad
 * document blanks a whole route rather than one card.
 *
 * Returning `undefined` rather than an Invalid Date is the load-bearing part:
 * callers write `timestampToDate(x) ?? fallback`, and an Invalid Date is TRUTHY,
 * so it would sail past the `??` into date-fns `format()`, which throws
 * `RangeError: Invalid time value`. Checking `timestampToMillis` alone would not
 * do it either — `NaN` and `Infinity` are both `typeof "number"`, so they pass a
 * type check and only turn into garbage once they reach `new Date`.
 */
const NANOS_PER_SECOND = 1_000_000_000;

/**
 * Whole-second millis for a serialized map whose `nanoseconds` are out of range,
 * or `undefined` when there is nothing to correct.
 *
 * A real Firestore `timestampValue` cannot carry bad nanoseconds — the backend
 * stores an int32 in `[0, 1e9)` and both SDKs reject anything else on write. A
 * hand-written map can, and those are precisely what this module exists to
 * survive. `timestampToMillis` adds `floor(nanos / 1e6)` unconditionally, so a
 * garbage sub-second component seconds away from local midnight would render the
 * NEXT day on a research record — silently wrong, which is worse than blank.
 *
 * Discarding the sub-second part still yields the correct calendar day, so this
 * clamps rather than rejects.
 */
const wholeSecondMillisIfNanosInvalid = (value: TimestampLike): number | undefined => {
  if (!value || typeof value !== "object" || "toMillis" in value) return undefined;
  const map = value as {
    seconds?: number;
    nanoseconds?: number;
    _seconds?: number;
    _nanoseconds?: number;
  };
  const [seconds, nanoseconds] =
    typeof map.seconds === "number"
      ? [map.seconds, map.nanoseconds]
      : [map._seconds, map._nanoseconds];

  if (typeof seconds !== "number" || typeof nanoseconds !== "number") return undefined;
  // In range: leave it to `timestampToMillis`, which keeps the precision.
  if (nanoseconds >= 0 && nanoseconds < NANOS_PER_SECOND) return undefined;
  return seconds * 1000;
};

export const timestampToDate = (value: TimestampLike): Date | undefined => {
  const millis = wholeSecondMillisIfNanosInvalid(value) ?? timestampToMillis(value);
  if (millis === undefined) return undefined;
  const date = new Date(millis);
  // Catches NaN, ±Infinity, and anything outside the ±8.64e15 ms Date range —
  // every one of those yields a NaN getTime().
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/** Display string for a timestamp, or `fallback` when it is missing or unusable. */
export const formatTimestampDate = (value: TimestampLike, fallback = "No date"): string =>
  timestampToDate(value)?.toLocaleDateString() ?? fallback;

/**
 * Sort key for a timestamp field: real millis, or `Number.POSITIVE_INFINITY` so
 * unusable values sort LAST.
 *
 * `timestampToMillis(...) ?? Infinity` is NOT enough — `NaN` is a number, so it
 * passes the `??`, and `NaN - x` is `NaN`, which the spec treats as `+0`. That
 * silently reorders otherwise-valid rows (the malformed one lands FIRST, the
 * opposite of the intent).
 */
export const timestampSortKey = (value: TimestampLike): number =>
  timestampToDate(value)?.getTime() ?? Number.POSITIVE_INFINITY;

export const formatTrialDateRange = (trial: Trial) => {
  const startDateString = formatTimestampDate(trial.startDate);
  const endDateString = trial.endDate ? formatTimestampDate(trial.endDate) : "Ongoing";

  return `${startDateString} - ${endDateString}`;
};

/**
 * Generates a duplicate task name with current date appended.
 * If name already ends with " - YYYY/MM/DD", replaces the date portion.
 * @example "Harvest Data" -> "Harvest Data - 2024/01/15"
 * @example "Harvest Data - 2024/01/10" -> "Harvest Data - 2024/01/15"
 */
/**
 * Case-insensitive check for duplicate names.
 * When editing, pass `excludeName` to allow the current entity to keep its own name.
 */
export function isDuplicateName(
  name: string,
  existingNames: string[],
  excludeName?: string,
): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return false;
  const names = excludeName
    ? existingNames.filter(n => n.toLowerCase() !== excludeName.toLowerCase())
    : existingNames;
  return names.some(n => n.trim().toLowerCase() === normalized);
}

export function generateDuplicateTaskName(originalName: string): string {
  const datePattern = / - \d{4}\/\d{2}\/\d{2}$/;
  const today = new Date();
  const formattedDate = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;

  if (datePattern.test(originalName)) {
    return originalName.replace(datePattern, ` - ${formattedDate}`);
  }
  return `${originalName} - ${formattedDate}`;
}
